import { useEffect, useRef, useState } from 'react';
import type { AssigneeSuggestion } from '@/lib/api';
import { useNotifications } from '@/contexts/NotificationsContext';

/**
 * Хук для автоподсказок исполнителя: дебаунс на 150мс, общий бэкенд-эндпойнт
 * `/api/me/users-suggest`. Используется и `AssigneeTokens`, и `SingleAssigneePicker`.
 *
 * - Пока `open === false` — ничего не грузит и возвращает пустой массив.
 * - При `open === true` сразу загружает первую партию, а затем реагирует на ввод.
 */
export function useAssigneeSuggestions(open: boolean, query: string): AssigneeSuggestion[] {
  const { fetchSuggestions } = useNotifications();
  const [items, setItems] = useState<AssigneeSuggestion[]>([]);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (!open) {
      setItems([]);
      return;
    }
    if (debounceRef.current != null) {
      window.clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      fetchSuggestions(query.trim())
        .then((list) => setItems(list))
        .catch(() => setItems([]));
    }, 150);
    return () => {
      if (debounceRef.current != null) window.clearTimeout(debounceRef.current);
    };
  }, [open, query, fetchSuggestions]);

  return items;
}
